import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { Panel, ActionButton, TextField, LocalizedField } from "./_shared";
import IconPicker from "@/components/IconPicker";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = (pid) => ({ property_id: pid, slug: "", name: { it: "", en: "", de: "" }, icon: "MapPin", accent_color: null, is_public: true, order: 99 });

export default function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [propertyId, setPropertyId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async (pid) => {
    const [pRes, cRes] = await Promise.all([
      api.get("/admin/properties"),
      api.get(`/admin/categories${pid ? `?property_id=${pid}` : ""}`),
    ]);
    setProperties(pRes.data || []);
    setItems(cRes.data || []);
    if (!pid && pRes.data?.length) setPropertyId(pRes.data.find((p) => p.is_default)?.id || pRes.data[0].id);
  };
  useEffect(() => { load(propertyId); }, [propertyId]);

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/admin/categories/${editing.id}`, editing);
      } else {
        await api.post("/admin/categories", editing);
      }
      toast.success("Salvato");
      setEditing(null);
      load(propertyId);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questa categoria? Tutti i POI collegati verranno eliminati.")) return;
    await api.delete(`/admin/categories/${id}`);
    load(propertyId);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-editorial italic text-4xl text-ink-900">Categorie</h1>
          <p className="text-xs text-ink-500 mt-1">Le tile dell'home "Informazioni utili"</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={propertyId || ""} onChange={(e) => setPropertyId(e.target.value)} className="text-xs bg-white border border-ink-200/60 rounded-full px-3 py-2">
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <ActionButton onClick={() => setEditing(empty(propertyId))} testid="categories-add"><Plus className="w-3.5 h-3.5" /> Nuova</ActionButton>
        </div>
      </div>

      <Panel title="Elenco">
        <div className="divide-y divide-ink-200/30">
          {items.map((c) => (
            <div key={c.id} className="py-3 flex items-center justify-between" data-testid={`category-row-${c.slug}`}>
              <div>
                <div className="font-semibold">{c.name?.it || c.slug} <span className="text-xs text-ink-300">#{c.order}</span></div>
                <div className="text-xs text-ink-500">{c.slug} · icona {c.icon}</div>
              </div>
              <div className="flex gap-2">
                <ActionButton variant="secondary" testid={`category-edit-${c.slug}`} onClick={() => setEditing(c)}>Modifica</ActionButton>
                <ActionButton variant="danger" testid={`category-delete-${c.slug}`} onClick={() => remove(c.id)}><Trash2 className="w-3.5 h-3.5" /></ActionButton>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {editing && (
        <Panel title={editing.id ? "Modifica categoria" : "Nuova categoria"} action={
          <div className="flex gap-2">
            <ActionButton variant="secondary" onClick={() => setEditing(null)}>Annulla</ActionButton>
            <ActionButton onClick={save} testid="category-save"><Save className="w-3.5 h-3.5" /> Salva</ActionButton>
          </div>
        }>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
            <IconPicker value={editing.icon} onChange={(v) => setEditing({ ...editing, icon: v })} testid="category-icon-picker" />
            <TextField label="Ordine" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} />
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!editing.is_public} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} />
              <span className="text-sm">Visibile agli ospiti</span>
            </label>
            <div className="md:col-span-2">
              <LocalizedField label="Nome" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}
